import { formSessionError } from '@jukun/platform-sdk/core';
import { copyFormSessionData, runFormSessionOperation, sameFormSessionToken, validateDraftPageContext, type FormSessionToken } from '@jukun/platform-sdk/shell';
import type { PortalApplication, PortalIdentity } from '../../../model/portalModels';
import type { FormSelectionTicket, PortalFormSessionManager } from '../../../runtime/formSessionManager';
import { FORM_SESSION_OPEN_BUDGET_MS, FormLeaveCancelledError } from '../../../runtime/formSessionManager';
import { draftsApi } from '../api/draftsApi';
import type { PortalDraftDetail } from '../model/drafts';

export type DraftNavigationOptions = Readonly<{ force?: boolean; replace?: boolean; refresh?: boolean }>;
export type DraftNavigate = (path: string, options?: DraftNavigationOptions) => Promise<boolean>;
export type PreparedDraftSelection = Readonly<{
  draftId: string; ticket: FormSelectionTicket; detail: PortalDraftDetail;
  token?: FormSessionToken; sourceFailure?: unknown; authorization: 'SESSION' | 'LEAVE';
}>;
type DraftPreparationNavigation = Readonly<{ isCurrent: () => boolean; leaveCurrent: () => Promise<boolean> }>;
export function isBuiltInTenantDraft(detail: PortalDraftDetail) {
  const context = detail.flowPageContext;
  return context.applicationCode === 'ADMIN' && context.processId === 'ADMIN_TENANT_APPLICATION_V2'
    && context.businessObjectType === 'TENANT_APPLICATION';
}
export function hasLocalDraftSave(detail: PortalDraftDetail) {
  return detail.flowPageContext.availableActions.some(action => action.actionCode === 'SAVE' && action.operationType === 'SHELL_LOCAL');
}
export function validateDraftDetail(value: PortalDraftDetail, draftId: string, identity: PortalIdentity): PortalDraftDetail {
  const detail = copyFormSessionData(value);
  const context = validateDraftPageContext(detail.flowPageContext, detail.summary.applicationCode, identity.actorPublicId);
  if (detail.summary.draftPublicId !== draftId || context.draftContext.draftPublicId !== draftId
      || detail.summary.businessObjectPublicId !== context.businessObjectPublicId
      || detail.summary.businessObjectType !== context.businessObjectType || detail.summary.processId !== context.processId
      || detail.summary.version !== context.draftContext.expectedDraftVersion) throw formSessionError('INPUT_INVALID');
  return detail;
}

/** Same selection/leave transaction as task forms; no second draft lifecycle. */
export class DraftFormSelectionCoordinator {
  private readonly requests = new WeakMap<FormSelectionTicket, Promise<PreparedDraftSelection>>();
  private readonly navigations = new WeakMap<FormSelectionTicket, DraftPreparationNavigation>();
  private readonly failed = new WeakSet<FormSelectionTicket>();
  constructor(private readonly manager: PortalFormSessionManager,
    private readonly applications: () => readonly PortalApplication[], private readonly identity: () => PortalIdentity,
    private readonly readDetail = draftsApi.detail) {}

  prepare(draftId: string, options: Readonly<{ force?: boolean; isCurrent: () => boolean; leaveCurrent: () => Promise<boolean> }>) {
    const target = { contextType: 'DRAFT' as const, subjectPublicId: draftId };
    let ticket = this.manager.beginSelection(target, { force: options.force });
    if (this.failed.has(ticket)) ticket = this.manager.beginSelection(target, { force: true });
    // Several route transactions can await one selection. Keep their route owner
    // separate from the shared ticket, whose generation and preparation stay unchanged.
    this.navigations.set(ticket, options);
    const cached = this.requests.get(ticket);
    if (cached) return cached;
    const request = this.readAndOpen(draftId, ticket);
    this.requests.set(ticket, request);
    void request.then(selection => { if (selection.sourceFailure) this.failed.add(ticket); }, () => { this.failed.add(ticket); });
    return request;
  }

  private async readAndOpen(draftId: string, ticket: FormSelectionTicket): Promise<PreparedDraftSelection> {
    // App checks each caller's transaction before route commit. Superseding that
    // caller alone must not reject the preparation still owned by this ticket.
    const assertCurrent = () => { if (!this.manager.isCurrentSelection(ticket)) throw formSessionError('STALE'); };
    const value = await runFormSessionOperation(operation => this.readDetail(draftId, { signal: operation.signal }),
      { signal: ticket.signal, remainingMs: FORM_SESSION_OPEN_BUDGET_MS }, 'OPEN_TIMEOUT');
    assertCurrent();
    const detail = validateDraftDetail(value, draftId, this.identity());
    if (isBuiltInTenantDraft(detail)) {
      const navigation = this.navigations.get(ticket);
      if (!navigation?.isCurrent()) throw formSessionError('STALE');
      if (!await navigation.leaveCurrent()) throw new FormLeaveCancelledError();
      if (!navigation.isCurrent()) throw formSessionError('STALE');
      return Object.freeze({ draftId, ticket, detail, authorization: 'LEAVE' });
    }
    const initial = this.manager.getSnapshot().activeSession;
    let seenToken: string | undefined;
    const removeEligibility = this.manager.subscribe(() => {
      const active = this.manager.getSnapshot().activeSession;
      if (!active || active.token.generation !== ticket.generation || active.token.sessionId === initial?.token.sessionId
          || seenToken === active.token.sessionId) return;
      seenToken = active.token.sessionId;
      this.manager.setSaveEligibility(active.token, false);
    });
    try {
      const context = detail.flowPageContext;
      const application = this.applications().find(candidate => candidate.appCode === context.applicationCode && candidate.status === 'ENABLED');
      if (!application) throw formSessionError('INPUT_INVALID');
      const token = await this.manager.open(ticket, { application, context, pageRoute: context.pageRoute, targetRef: detail.summary.draftPublicId });
      assertCurrent();
      return Object.freeze({ draftId, ticket, detail, token, authorization: 'SESSION' });
    } catch (sourceFailure) {
      assertCurrent();
      const active = this.manager.getSnapshot().activeSession;
      if (initial && active && sameFormSessionToken(initial.token, active.token)) throw sourceFailure;
      if (active && active.token.generation !== ticket.generation) throw sourceFailure;
      return Object.freeze({ draftId, ticket, detail, token: active?.token, sourceFailure, authorization: 'SESSION' });
    } finally { removeEligibility(); }
  }
  isCurrent(selection: PreparedDraftSelection) {
    if (selection.authorization === 'LEAVE') return true;
    if (!this.manager.isCurrentSelection(selection.ticket)) return false;
    const active = this.manager.getSnapshot().activeSession;
    return selection.token ? !!active && sameFormSessionToken(selection.token, active.token) : !active;
  }
}
